import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { ScrollbarWrapper } from "../../../../../../components/ScrollbarWrapper";
import Scrollbar from "smooth-scrollbar";

const Select = ({ name, label, selectedValue, disabled = false, required, error, options = [], onChange, searchInDropdown = true, searchVisible = true, hint, allowAppendText = false, }) => {
    const [selectDown, isSelectDown] = useState(false);
    const [selectedOption, setSelectedOption] = useState(selectedValue);
    const [selectedOptionName, setSelectedOptionName] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const selectRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [scrollActive, setScrollActive] = useState(true);
    const [hasVisibleTrack, setHasVisibleTrack] = useState(false);
    const [resetValue, setResetValue] = useState("");

    const selectToggle = () => {
        isSelectDown(!selectDown);
    };

    const handleOptionChange = (option) => {
        const comparedOption = options.find((o) => o.id === option);
        if (comparedOption) {
            if (allowAppendText) {
                setSelectedOptionName(comparedOption.name);
                setSelectedOption(comparedOption.name);
            } else {
                setSelectedOptionName(comparedOption.name);
                setSelectedOption(comparedOption.id);
            }
        }
        onChange({ target: { name, value: allowAppendText ? comparedOption.name : comparedOption.id } });
        isSelectDown(false);
        setResetValue("");
        setSearchTerm("");
    };

    const handleAppendChange = (event) => {
        const value = event.target.value;
        setSelectedOptionName(value);
        setSelectedOption(value);
        onChange({ target: { name, value } });
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setResetValue(event.target.value);
    };

    const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            isSelectDown(false);
        }
    };

    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleResize);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (allowAppendText) return;

        const comparedOption = options.find((o) => o.id === selectedValue);
        if (comparedOption) {
            setSelectedOptionName(comparedOption.name);
            setSelectedOption(comparedOption.id);
        } else {
            setSelectedOptionName("");
            setSelectedOption("");
        }
    }, [selectedValue, options, allowAppendText]);

    useLayoutEffect(() => {
        if (scrollbarRef?.current) {
            Scrollbar.init(scrollbarRef?.current, {
                damping: 0.1,
                thumbMinSize: 20,
                renderByPixels: true,
                continuousScrolling: "auto",
                alwaysShowTracks: true,
                delegateTo: null,
                plugins: {},
            });

            function setupScrollbar(options = {}) {
                const scrollbar = Scrollbar.get(scrollbarRef?.current);
                if (!scrollbar) return;

                if (options.removeXTrack && scrollbar.track.xAxis.element) {
                    scrollbar.track.xAxis.element.remove();
                }
                if (options.removeYTrack && scrollbar.track.yAxis.element) {
                    scrollbar.track.yAxis.element.remove();
                }

                let isScrolling;
                scrollbar.addListener(() => {
                    setScrollActive(true);

                    clearTimeout(isScrolling);
                    isScrolling = setTimeout(() => {
                        setScrollActive(false);
                    }, 200);
                });

                const toggleScrollbarClass = () => {
                    const tracks = scrollbarRef?.current?.querySelectorAll(`#mainContentScrollbar > .scrollbar-track`);

                    const hasVisibleTrack = tracks?.length > 0 && Array.from(tracks).some((track) => getComputedStyle(track).display === "block");

                    if (hasVisibleTrack) {
                        setHasVisibleTrack(true);
                    } else {
                        setHasVisibleTrack(false);
                    }
                };

                const tracks = scrollbarRef?.current?.querySelectorAll(`#mainContentScrollbar > .scrollbar-track`);
                const observer = new MutationObserver(() => {
                    toggleScrollbarClass();
                });

                tracks.forEach((track) => {
                    observer.observe(track, { attributes: true, attributeFilter: ["style"] });
                });

                if (!!options?.observeResize) {
                    const resizeTarget = document.querySelector(options.observeResize);
                    if (resizeTarget) {
                        const resizeObserver = new ResizeObserver(() => {
                            setTimeout(() => {
                                scrollbar.update();
                            }, 1000);
                            toggleScrollbarClass();
                        });
                        resizeObserver.observe(resizeTarget);
                    }
                }
            }

            setupScrollbar(options);
        }
    }, []);
    // console.log(resetValue);

    useEffect(() => {
        if (allowAppendText) {
            setSelectedOptionName(selectedValue || "");
            setSelectedOption(selectedValue || "");
        }
    }, [selectedValue, allowAppendText]);

    return (
        <>
            <label htmlFor={`input-${name}`} className={`${required ? "input-required" : ""}`}>
                {label}
            </label>
            <div className="dropdown dropdown--select" style={{ width: "100%", cursor: `${!disabled ? "pointer" : "default"}` }} ref={selectRef}>
                 {allowAppendText ? (
                    <input
                        type="text"
                        className="dropdown__menu__search--input"
                        value={selectedOptionName}
                        disabled={disabled}
                        onChange={handleAppendChange}
                        onClick={selectToggle}
                    />
                ) : searchInDropdown ? (
                    <div className={`${!disabled ? "dropdown__toggle" : "dropdown__toggle--disabled"}`} onClick={selectToggle}>
                        {selectedOptionName || ""}
                    </div>
                ) : (
                    <input
                        type="text"
                        className="dropdown__menu__search--input"
                        placeholder={selectedOptionName || ""}
                        onChange={handleSearchChange}
                        onClick={selectToggle}
                        value={resetValue}
                    />
                )}
                <div className={`dropdown__menu ${selectDown && !disabled ? "active" : ""}`}>
                    {searchInDropdown && searchVisible ? (
                        <input type="text" className="dropdown__menu__search" placeholder="Search option" onChange={handleSearchChange} />
                    ) : (
                        ""
                    )}
                    <div
                        ref={scrollbarRef}
                        id="mainContentScrollbar"
                        className={`dropdown__menu__wrap ${scrollActive ? "active-scroll" : ""} ${hasVisibleTrack ? "scrollbar-on" : ""}`}
                        data-scrollbar
                    >
                        <div className="scroll-content">
                            {options.map((option, index) => (
                                <div
                                    className="dropdown__menu__item"
                                    key={`${option?.id}-${index}`}
                                    data-value={option?.name}
                                    onClick={() => handleOptionChange(option?.id)}
                                    style={{ display: option.name.toLowerCase().includes(searchTerm.toLowerCase()) ? "" : "none" }}
                                >
                                    <span>{option?.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {hint && <div className="hint-message-text">{hint}</div>}
            {error && <div className="error-message-text">{error}</div>}
        </>
    );
};

export default Select;
